import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

const PARAM = "setupTask";

/**
 * Read + clear the `?setupTask=<taskId>` query param. The widget appends
 * this param when launching a task; the contextual modal renderer reads
 * it to decide whether to open; closing the modal removes it.
 *
 * Hook returned API is stable across renders so consumers can pass into
 * effect deps without forcing re-runs.
 */
export interface ContextualModalLifecycle {
  /** The task id from the URL, or null when no `?setupTask=` is present. */
  activeTaskId: string | null;
  /** Strip the param from the URL (closes the modal as a side effect). */
  clearActiveTaskId: () => void;
}

export function useContextualModalLifecycle(): ContextualModalLifecycle {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTaskId = useMemo(() => searchParams.get(PARAM), [searchParams]);

  const clearActiveTaskId = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete(PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return { activeTaskId, clearActiveTaskId };
}
